import React, { useEffect, useMemo } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Typography } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  mapPilotStatusToOptions,
  type PilotStationType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface UpdateTimesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  visibleFields: Array<keyof TimesFormData>;
  labelKeys?: Partial<Record<keyof TimesFormData, string>>;
  movementDetails?: {
    eta?: string | null;
    etd?: string | null;
    ets?: string | null;
    etc?: string | null;
    pilotUnderway?: string | null;
    permissionToSail?: string | null;
    anchorTime?: string | null;
    anchorAweighTime?: string | null;
    pob?: string | null;
    firstLineAshore?: string | null;
    allFast?: string | null;
    pilotDisembark?: string | null;
    ats?: string | null;
    allLinesClear?: string | null;
    allClear?: string | null;
    pilotStationCode?: string | null;
    movementType?: string | null;
  };
  onSave: (times: {
    eta: Date | null | undefined;
    etd: Date | null | undefined;
    ets: Date | null | undefined;
    etc: Date | null | undefined;
    pilotUnderway: Date | null | undefined;
    permissionToSail: Date | null | undefined;
    anchorTime: Date | null | undefined;
    anchorAweighTime: Date | null | undefined;
    pob: Date | null | undefined;
    firstLineAshore: Date | null | undefined;
    allFast: Date | null | undefined;
    pilotDisembark: Date | null | undefined;
    ats: Date | null | undefined;
    allLinesClear: Date | null | undefined;
    allClear: Date | null | undefined;
    pilotStationCode: string | null | undefined;
  }) => void;
}

const timesSchema = z.object({
  eta: z.date().nullable().optional(),
  etd: z.date().nullable().optional(),
  ets: z.date().nullable().optional(),
  etc: z.date().nullable().optional(),
  pilotUnderway: z.date().nullable().optional(),
  permissionToSail: z.date().nullable().optional(),
  anchorTime: z.date().nullable().optional(),
  anchorAweighTime: z.date().nullable().optional(),
  pob: z.date().nullable().optional(),
  firstLineAshore: z.date().nullable().optional(),
  allFast: z.date().nullable().optional(),
  pilotDisembark: z.date().nullable().optional(),
  ats: z.date().nullable().optional(),
  allLinesClear: z.date().nullable().optional(),
  allClear: z.date().nullable().optional(),
  pilotStationCode: z.string().nullable().optional(),
});

type TimesFormData = z.infer<typeof timesSchema>;

const LANG_BASE = "vessel-call.movement-services.update-form.fields";

const defaultLabelKeys: Record<keyof TimesFormData, string> = {
  eta: `${LANG_BASE}.eta-readiness`,
  etd: `${LANG_BASE}.etd-readiness`,
  ets: `${LANG_BASE}.ets-readiness`,
  etc: `${LANG_BASE}.etc-completion`,
  pilotUnderway: `${LANG_BASE}.pilot-underway`,
  permissionToSail: `${LANG_BASE}.permission-to-sail`,
  anchorTime: `${LANG_BASE}.anchor-time`,
  anchorAweighTime: `${LANG_BASE}.anchor-aweigh-time`,
  pob: `${LANG_BASE}.pob`,
  firstLineAshore: `${LANG_BASE}.first-line-ashore`,
  allFast: `${LANG_BASE}.all-fast`,
  pilotDisembark: `${LANG_BASE}.pilot-disembarked`,
  ats: `${LANG_BASE}.actual-time-start`,
  allLinesClear: `${LANG_BASE}.all-lines-clear`,
  allClear: `${LANG_BASE}.all-clear`,
  pilotStationCode: `${LANG_BASE}.pilot-boarding-area`,
};

const UpdateTimesDrawer: React.FC<UpdateTimesDrawerProps> = ({
  isOpen,
  onClose,
  visibleFields,
  labelKeys,
  movementDetails,
  onSave,
}) => {
  const { t } = useTranslation();

  const { options: stations } = useFormattedReference<PilotStationType>(
    REFERENCE_DATA_TYPES.PILOT_STATIONS,
    "bff-vessel-call",
  );

  const pilotStationOptions = useMemo(() => mapPilotStatusToOptions(stations), [stations]);

  const isAnchorageToBerth = movementDetails?.movementType === "ANCHORAGE_TO_BERTH";

  const form = useForm<TimesFormData>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(timesSchema),
    shouldUnregister: true,
    defaultValues: {
      eta: null,
      etd: null,
      ets: null,
      etc: null,
      pilotUnderway: null,
      permissionToSail: null,
      anchorTime: null,
      anchorAweighTime: null,
      pob: null,
      firstLineAshore: null,
      allFast: null,
      pilotDisembark: null,
      ats: null,
      allLinesClear: null,
      allClear: null,
      pilotStationCode: null,
    },
  });

  // Prepopulate fields from movementDetails
  useEffect(() => {
    if (movementDetails && isOpen) {
      form.reset({
        eta: movementDetails.eta ? new Date(movementDetails.eta) : null,
        etd: movementDetails.etd ? new Date(movementDetails.etd) : null,
        ets: movementDetails.ets ? new Date(movementDetails.ets) : null,
        etc: movementDetails.etc ? new Date(movementDetails.etc) : null,
        pilotUnderway: movementDetails.pilotUnderway
          ? new Date(movementDetails.pilotUnderway)
          : null,
        permissionToSail: movementDetails.permissionToSail
          ? new Date(movementDetails.permissionToSail)
          : null,
        anchorTime: movementDetails.anchorTime ? new Date(movementDetails.anchorTime) : null,
        anchorAweighTime: movementDetails.anchorAweighTime
          ? new Date(movementDetails.anchorAweighTime)
          : null,
        pob: movementDetails.pob ? new Date(movementDetails.pob) : null,
        firstLineAshore: movementDetails.firstLineAshore
          ? new Date(movementDetails.firstLineAshore)
          : null,
        allFast: movementDetails.allFast ? new Date(movementDetails.allFast) : null,
        pilotDisembark: movementDetails.pilotDisembark
          ? new Date(movementDetails.pilotDisembark)
          : null,
        ats: movementDetails.ats ? new Date(movementDetails.ats) : null,
        allLinesClear: movementDetails.allLinesClear
          ? new Date(movementDetails.allLinesClear)
          : null,
        allClear: movementDetails.allClear ? new Date(movementDetails.allClear) : null,
        pilotStationCode: movementDetails.pilotStationCode,
      });
    }
  }, [movementDetails, isOpen, form]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const shouldShowField = (fieldName: keyof TimesFormData) => visibleFields.includes(fieldName);

  const getLabel = (fieldName: keyof TimesFormData) =>
    t(labelKeys?.[fieldName] ?? defaultLabelKeys[fieldName]);

  const onValid = (data: TimesFormData) => {
    onSave({
      eta: data.eta,
      etd: data.etd,
      ets: data.ets,
      etc: data.etc,
      pilotUnderway: data.pilotUnderway,
      permissionToSail: data.permissionToSail,
      anchorTime: data.anchorTime,
      anchorAweighTime: data.anchorAweighTime,
      pob: data.pob,
      firstLineAshore: data.firstLineAshore,
      allFast: data.allFast,
      pilotDisembark: data.pilotDisembark,
      ats: data.ats,
      allLinesClear: data.allLinesClear,
      allClear: data.allClear,
      pilotStationCode: data.pilotStationCode,
    });
    onClose();
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  // Used to add conditional placeholders for specific movement types
  const isSeaToBerth = movementDetails?.movementType === "SEA_TO_BERTH";
  const isBerthToSea = movementDetails?.movementType === "BERTH_TO_SEA";
  const isBerthToBerth = movementDetails?.movementType === "BERTH_TO_BERTH";

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        isChildDrawer
        title={
          <div className="flex items-center gap-2">
            <ArrowBackIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("vessel-call.movement-services.close-form.update-times-drawer.title")}
            </Typography>
          </div>
        }
        onClose={handleClose}
        submitLabel={t(
          "vessel-call.movement-services.close-form.update-times-drawer.update-button",
        )}
        onSubmit={onSubmit}
      >
        <div className="px-6 py-4 flex flex-col gap-4">
          {/* Show only fields that are in the CLOSE endpoint body */}
          <div className="grid grid-cols-2 gap-4" data-testid="times-fields-grid">
            {shouldShowField("etd") && (
              <FormItem
                name="etd"
                type="date-picker"
                label={getLabel("etd")}
                testId="etd-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("eta") && (
              <FormItem
                name="eta"
                type="date-picker"
                label={getLabel("eta")}
                testId="eta-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("ets") && (
              <FormItem
                name="ets"
                type="date-picker"
                label={getLabel("ets")}
                testId="ets-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("etc") && (
              <FormItem
                name="etc"
                type="date-picker"
                label={getLabel("etc")}
                testId="etc-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("pilotStationCode") && (
              <FormItem
                name="pilotStationCode"
                type="autocomplete"
                label={getLabel("pilotStationCode")}
                testId="pilot-station-autocomplete-input"
                options={pilotStationOptions}
              />
            )}
            {!isAnchorageToBerth && shouldShowField("etc") && isBerthToSea && (
              <div className="h-full" aria-hidden="true" data-testid="etc-placeholder" />
            )}
            {shouldShowField("pilotUnderway") && (
              <FormItem
                name="pilotUnderway"
                type="date-picker"
                label={getLabel("pilotUnderway")}
                testId="pilot-underway-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("pilotUnderway") && isBerthToBerth && (
              <div className="h-full" aria-hidden="true" data-testid="pilot-underway-placeholder" />
            )}
            {shouldShowField("permissionToSail") && (
              <FormItem
                name="permissionToSail"
                type="date-picker"
                label={getLabel("permissionToSail")}
                testId="permission-to-sail-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("anchorTime") && (
              <FormItem
                name="anchorTime"
                type="date-picker"
                label={getLabel("anchorTime")}
                testId="anchor-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("anchorAweighTime") && (
              <FormItem
                name="anchorAweighTime"
                type="date-picker"
                label={getLabel("anchorAweighTime")}
                testId="anchor-aweigh-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("pob") && (
              <FormItem
                name="pob"
                type="date-picker"
                label={getLabel("pob")}
                testId="pob-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("allClear") && (
              <FormItem
                name="allClear"
                type="date-picker"
                label={getLabel("allClear")}
                testId="all-clear-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("firstLineAshore") && (
              <FormItem
                name="firstLineAshore"
                type="date-picker"
                label={getLabel("firstLineAshore")}
                testId="first-line-ashore-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {!isAnchorageToBerth && shouldShowField("firstLineAshore") && isSeaToBerth && (
              <div
                className="h-full"
                aria-hidden="true"
                data-testid="first-line-ashore-placeholder"
              />
            )}
            {shouldShowField("allFast") && (
              <FormItem
                name="allFast"
                type="date-picker"
                label={getLabel("allFast")}
                testId="all-fast-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("pilotDisembark") && (
              <FormItem
                name="pilotDisembark"
                type="date-picker"
                label={getLabel("pilotDisembark")}
                testId="pilot-disembark-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("ats") && (
              <FormItem
                name="ats"
                type="date-picker"
                label={getLabel("ats")}
                testId="ats-time-date-input"
                timePicker
                combinedInput
              />
            )}
            {shouldShowField("allLinesClear") && (
              <FormItem
                name="allLinesClear"
                type="date-picker"
                label={getLabel("allLinesClear")}
                testId="all-lines-clear-time-input"
                timePicker
                combinedInput
              />
            )}
          </div>
        </div>
      </SideDrawer>
    </FormProvider>
  );
};

export default UpdateTimesDrawer;
